import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width,
  height,
  rounded = 'md'
}) => {
  const roundedClasses = {
    none: '',
    sm: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full'
  };

  const style: React.CSSProperties = {};
  if (width) {style.width = typeof width === 'number' ? `${width}px` : width;}
  if (height) {style.height = typeof height === 'number' ? `${height}px` : height;}

  return (
    <div
      className={`bg-gray-200 animate-pulse ${roundedClasses[rounded]} ${className}`}
      style={style}
    />
  );
};

interface InventoryOverviewSkeletonProps {
  showStats?: boolean;
  showFilters?: boolean;
  showTable?: boolean;
  rowsCount?: number;
}

export const InventoryOverviewSkeleton: React.FC<InventoryOverviewSkeletonProps> = ({
  showStats = true,
  showFilters = true,
  showTable = true,
  rowsCount = 5
}) => {
  return (
    <div className="space-y-6">
      {/* Stats Skeleton */}
      {showStats && (
        <div className="p-6 bg-white rounded-lg shadow">
          <div className="flex items-center gap-2 mb-6">
            <Skeleton width={20} height={20} rounded="md" />
            <Skeleton width={160} height={24} rounded="md" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="text-center p-4 bg-gray-50 rounded-lg">
                <Skeleton width={24} height={24} rounded="md" className="mx-auto mb-2" />
                <Skeleton width={48} height={32} rounded="md" className="mx-auto mb-2" />
                <Skeleton width={80} height={16} rounded="md" className="mx-auto" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters Skeleton */}
      {showFilters && (
        <div className="p-6 bg-white rounded-lg shadow">
          <div className="flex flex-wrap gap-4 items-end">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="min-w-48">
                <Skeleton width={80} height={16} rounded="md" className="mb-2" />
                <Skeleton width="100%" height={40} rounded="md" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table Skeleton */}
      {showTable && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 bg-gray-50 border-b">
            <Skeleton width={120} height={24} rounded="md" className="mb-1" />
            <Skeleton width={200} height={16} rounded="md" />
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {[...Array(rowsCount)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-4 border rounded-lg">
                  <Skeleton width={40} height={40} rounded="lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton width={128} height={16} rounded="md" />
                    <Skeleton width={96} height={12} rounded="md" />
                  </div>
                  <Skeleton width={64} height={24} rounded="md" />
                  <Skeleton width={80} height={24} rounded="md" />
                  <Skeleton width={100} height={24} rounded="md" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Skeleton;
